import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { api, mediaSrc, money } from "@/lib/api";

const LOGO_ICON = "/logo-emblem.png";
const HERO_IMG = "https://images.unsplash.com/photo-1646009759852-f035b994a783?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsZWdvJTIwc3RhciUyMHdhcnMlMjBzZXQlMjBjb2xsZWN0b3J8ZW58MHx8fHwxNzg4NjE1OTA4fDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api.get("/items")
      .then(({ data }) => setFeatured(data.find((i) => i.is_featured) || null))
      .catch(() => {});
  }, []);

  const openFeatured = () => {
    if (!featured) return;
    window.dispatchEvent(new CustomEvent("open-item-detail", { detail: featured.id }));
    document.getElementById("stock")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 8, y: px * 10 });
  };

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#0A0A0A]/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728] flex items-center gap-3"
            data-testid="hero-badge"
          >
            <span className="inline-block w-10 h-px bg-[#B38728]" />
            01 — Buy • Sell • Trade • Collect
          </motion.p>

          <h1 className="mt-8 font-serif font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <Line delay={0.35}>THE CURATED VAULT</Line>
            <Line delay={0.5}>FOR <span className="italic gold-text">LEGO</span> <span className="italic">GRAILS</span></Line>
            <Line delay={0.65}>& MINIFIGURE <span className="italic gold-text">ROYALTY</span></Line>
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="origin-left mt-8 h-px w-40 bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#AA771C]"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-6 text-base md:text-lg text-[#5C564C] max-w-xl leading-relaxed"
          >
            Stud Exchange authenticates, values and trades the world's most sought-after LEGO sets and minifigures.
            Every piece verified. Every deal sealed with trust.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#stock"
              data-testid="hero-cta-explore"
              className="group bg-[#0A0A0A] text-[#F3EDE3] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors flex items-center gap-3"
            >
              Explore Stock
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </a>
            <a
              href="#valuation"
              data-testid="hero-cta-valuation"
              className="border border-[#0A0A0A] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] hover:bg-[#0A0A0A] hover:text-[#F3EDE3] transition-colors flex items-center gap-3"
            >
              <Sparkles className="w-4 h-4 text-[#B38728]" />
              Free Valuation
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            className="relative"
          >
            <div
              onClick={featured ? openFeatured : undefined}
              title={featured ? "View this piece" : undefined}
              data-testid="hero-featured-card"
              className={`border border-[#DCD3C3] bg-white p-3 shadow-2xl transition-transform duration-200 ease-out${featured ? " cursor-pointer hover:border-[#B38728]" : ""}`}
              style={{ transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
            >
              <div className="overflow-hidden">
                <img
                  src={featured && featured.image_url ? mediaSrc(featured.image_url) : HERO_IMG}
                  alt={featured ? `${featured.title} — featured from the Stud Exchange vault` : "Rare LEGO Star Wars minifigure from the Stud Exchange vault"}
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="hero-featured-image"
                />
              </div>
              <div className="flex items-center justify-between px-2 pt-3 pb-1" data-testid="hero-featured-caption">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] truncate">
                  {featured ? `From the vault — ${featured.title}` : "Lot 002 — Chrome Vader"}
                </p>
                <p className="font-serif italic text-[#B38728] shrink-0 pl-3">
                  {featured ? (featured.in_stock ? money(featured.price) : "Sold") : "Grail"}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="absolute -bottom-10 -left-14 float-slow"
          >
            <img src={LOGO_ICON} alt="Stud Exchange minifig mark" className="w-28 h-28 rounded-full object-cover shadow-xl border border-[#D4AF37]/50" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
